import React, {useEffect, useState} from "react";
import {QualityFindingList, QualityFindingRestControllerApiFactory, QualitySummaryDto} from "arteco-api-client-ts";
import AppList, {AppListConfig} from "../../../../components/layout/list";
import dayjs from "dayjs";
import {Box, Chip, FormControlLabel, Grid, MenuItem, Switch, TableCell, TextField, Typography} from "@mui/material";
import {useApiManager} from "../../../../components/utils/api";
import {COLOR_DISMISSED} from "../../../../components/layout/colors";

const TIPO: Record<string, string> = {
	NO_CONFORMIDAD: "No conformidad",
	INCIDENTE_SEGURIDAD: "Incidente",
	RECLAMACION: "Reclamación",
	OPORTUNIDAD_MEJORA: "Mejora",
};

const ESTADO: Record<string, string> = {
	ABIERTA: "Abierta",
	EN_ANALISIS: "En análisis",
	EN_TRATAMIENTO: "En tratamiento",
	PENDIENTE_EFICACIA: "Pendiente de eficacia",
	CERRADA: "Cerrada",
};

const statusColor: Record<string, "default" | "success" | "warning" | "error" | "info"> = {
	ABIERTA: "error",
	EN_ANALISIS: "warning",
	EN_TRATAMIENTO: "warning",
	PENDIENTE_EFICACIA: "info",
	CERRADA: "success",
};

/** Registro de no conformidades, incidentes, reclamaciones y mejoras (#13718). */
export const QualityFindingListPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(QualityFindingRestControllerApiFactory);
	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState<number>(currentYear);
	const [open, setOpen] = useState<boolean>(false);
	const [trigger, setTrigger] = useState(0);
	const [summary, setSummary] = useState<QualitySummaryDto>();

	useEffect(() => {
		apiManager.execute(ctrl.summaryQualityFinding(year)).then(setSummary);
	}, [year]);

	const hoy = new Date().toISOString().substring(0, 10);

	const listConfig: AppListConfig<QualityFindingList> = {
		title: "No conformidades, incidentes y mejoras",
		onClick: "/app/admin/compliance/finding",
		headers: [
			{title: "Detectado"},
			{title: "Tipo"},
			{title: "Título"},
			{title: "Responsable"},
			{title: "Plazo"},
			{title: "Estado"},
		],
		decorator: (bean: QualityFindingList) => {
			const vencida = bean.status !== "CERRADA" && bean.dueDate && String(bean.dueDate) < hoy;
			return (
				<>
					<TableCell style={{color: bean.dismissed ? COLOR_DISMISSED : ""}}>{bean.detectedDate ? dayjs(bean.detectedDate).format("DD/MM/YYYY") : "—"}</TableCell>
					<TableCell>{TIPO[bean.type || ""] ?? bean.type}</TableCell>
					<TableCell>{bean.title}</TableCell>
					<TableCell>{(bean.responsible as any)?.surnamesAndName ?? "—"}</TableCell>
					<TableCell sx={{color: vencida ? "error.main" : undefined, fontWeight: vencida ? 600 : undefined}}>
						{bean.dueDate ? dayjs(bean.dueDate).format("DD/MM/YYYY") : "—"}
					</TableCell>
					<TableCell>
						<Chip size="small" label={ESTADO[bean.status || ""] ?? bean.status} color={statusColor[bean.status || ""] ?? "default"}/>
					</TableCell>
				</>
			);
		},
		supplier: async (currentPage, size, term, showDismissed) => {
			// Posicionales: year, type, status, open, term, showDismissed.
			return ctrl.pageQualityFinding(currentPage, size, undefined, year, undefined, undefined, open || undefined, term, showDismissed);
		},
		toolbar: (
			<Box sx={{mb: 2}}>
				<Grid container spacing={2} alignItems="center">
					<Grid item>
						<TextField select size="small" label="Año" value={year}
								   onChange={(e) => { setYear(Number(e.target.value)); setTrigger(trigger + 1); }}>
							{[currentYear, currentYear - 1, currentYear - 2].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
						</TextField>
					</Grid>
					<Grid item>
						<FormControlLabel label="Solo abiertas" control={
							<Switch checked={open} onChange={(e) => { setOpen(e.target.checked); setTrigger(trigger + 1); }}/>
						}/>
					</Grid>
				</Grid>
				{summary && (
					<Typography variant="body2" component="div" sx={{mt: 1, display: "flex", flexWrap: "wrap", gap: 1}}>
						{(summary.byType || []).filter(t => (t.total || 0) > 0).map(t => (
							<Chip key={t.type} size="small" variant="outlined"
								  label={`${TIPO[t.type || ""] ?? t.type}: ${t.total} (${t.open} abiertas, ${t.closedEffective} eficaces)`}/>
						))}
						{(summary.overdueFindings || 0) > 0 && <Chip size="small" color="error" label={`${summary.overdueFindings} fuera de plazo`}/>}
						{(summary.overdueActions || 0) > 0 && <Chip size="small" color="error" label={`${summary.overdueActions} acciones vencidas`}/>}
						{(summary.byType || []).every(t => !t.total) && <span>Sin registros en {year}.</span>}
					</Typography>
				)}
			</Box>
		),
	};

	return <AppList config={listConfig} trigger={trigger}/>;
};
